import {
	ChangeDetectionStrategy,
	Component,
	inject,
	Input,
	ViewChild,
} from '@angular/core';
import { PdftronComponent } from '@components/pdf-modal/pdftron/pdftron.component';
import { Logger } from '@core/logger';
import { removeUnwantedTags } from '@core/util/documents';
import { ICON } from '@core/util/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { ClipboardService } from '@services/clipboard/clipboard.service';
import { FileDownload } from '@state/file-download/fileDownload.model';
import { FileDownloadState } from '@state/file-download/fileDownload.state';
import { SelectedMatterDocumentsDispatch } from '@state/global/documents/selected-matter-documents.dispatch';
import {
	SelectedDocument,
	SelectedMatterDocumentsState,
} from '@state/global/documents/selected-matter-documents.state';
import { TagsSelector } from '@state/global/tags/tags.selector';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ToastService } from 'src/app/ui/toast/toast.service';
import { TestHooks } from './documents-matter-viewer.hooks';
import {
	DocumentsMatterViewerSelector,
	SelectedDocumentState,
} from './documents-matter-viewer.selector';

@UntilDestroy()
@Component({
	selector: 'app-documents-matter-viewer',
	templateUrl: './documents-matter-viewer.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class DocumentsMatterViewerComponent {
	testHooks = TestHooks;
	toastService = inject(ToastService);
	@Input() fileName: string;
	@Input() confirmationAction: (action: () => void) => void;
	@Input() disabledElements: string[] = [];

	@Select(SelectedMatterDocumentsState.getViewingDocumentPDF)
	pdf$: Observable<Blob>;
	@ViewChild('pdfViewer', { read: PdftronComponent })
	pdfViewer: PdftronComponent;

	@Select(FileDownloadState.getFileStatus)
	public fileDownloadStatusFn$: Observable<(filter: string) => FileDownload>;

	public wordDownloadStatus$: BehaviorSubject<boolean> = new BehaviorSubject(
		false,
	);

	@SelectSnapshot(SelectedMatterDocumentsState.getViewingDocument)
	viewingDocument: SelectedDocument;
	@Select(TagsSelector.getTagInjectedDocument) parsedDocument$: Observable<
		(doc: string) => string
	>;

	@Select(DocumentsMatterViewerSelector.documentStatus)
	documentStatus$: Observable<SelectedDocumentState>;

	ICON = ICON;
	DOCUMENT_STATUSES = SelectedDocumentState;
	sourceOfData: string;

	constructor(
		private selectedMatterDocumentDispatch: SelectedMatterDocumentsDispatch,
		private clipboardService: ClipboardService,
		private analytics: AnalyticsService,
	) {}

	documentLoaded() {}

	finishedRendering() {}

	print() {
		this.confirmationAction !== undefined
			? this.confirmationAction(() => this.pdfViewer.print())
			: this.pdfViewer.print();
	}

	downloadPDF() {
		this.confirmationAction !== undefined
			? this.confirmationAction(() => this.pdfViewer.download())
			: this.pdfViewer.download();
	}

	exportToWord() {
		try {
			const docId = this.viewingDocument.document.id;
			const exportAction = () => {
				this.analytics.logEvent(AnalyticsEvent.DOCUMENT_WORD_EXPORT_CLICK, {
					documentId: docId,
				});
				this.wordDownloadStatus$.next(true);
				this.selectedMatterDocumentDispatch.ExportViewingDocumentToWord(
					this.fileName,
					docId,
					(status) => {
						this.wordDownloadStatus$.next(false);
						if (status === 'success') {
							this.toastService.success(
								"Successfully exported to Word. Please check your browser's download history for the downloaded file.",
							);
						} else {
							this.toastService.error('Failed exporting to Word.');
						}
					},
				);
			};
			if (this.confirmationAction !== undefined) {
				this.confirmationAction(exportAction);
			} else {
				exportAction();
			}
		} catch (ex) {
			console.error(ex);
			this.wordDownloadStatus$.next(false);
			Logger.exception(ex, {
				message: 'Error exporting to Word',
				component: 'DocumentsMatterViewerComponent',
				method: 'exportToWord',
			});
			this.toastService.error('Failed exporting to Word.');
		}
	}

	public copyBodyContent(): void {
		if (this.confirmationAction !== undefined) {
			this.confirmationAction(() => {
				const bodyContent = removeUnwantedTags(
					this.viewingDocument.document.html,
				);

				this.getParsedDocument$(bodyContent)
					.pipe(untilDestroyed(this), take(1))
					.subscribe((html) => this.clipboardService.copyHtml(html, true));
			});
		} else {
			const bodyContent = removeUnwantedTags(
				this.viewingDocument.document.html,
			);

			this.getParsedDocument$(bodyContent)
				.pipe(untilDestroyed(this), take(1))
				.subscribe((html) => this.clipboardService.copyHtml(html, true));
		}
	}

	getParsedDocument$(document: string) {
		return this.parsedDocument$.pipe(
			untilDestroyed(this),
			map((_filter) => _filter(document)),
		);
	}
}
